/**
 * Analíticas de reservas: la misma pantalla que las otras dos, pero de la agenda.
 *
 * El panel sabía enseñar el tráfico y el dinero con tramos, comparación y
 * tendencia, y de las citas sólo tenía dos vistas de calendario. «¿Trabajé más
 * que el mes pasado?», «¿cuánta gente me falla?», «¿qué servicio llena la
 * agenda?», «¿con cuánta antelación me reservan?» eran preguntas que sólo se
 * podían responder contando cuadritos a mano.
 *
 * Comparte de forma deliberada todo lo que se puede compartir —el selector de
 * fechas, las tarjetas de cifra, la gráfica, las listas y el donut— para que las
 * tres se lean igual: quien ya sabe leer una sabe leer las otras.
 *
 * Tres cosas que conviene tener claras al mirarla:
 *
 * - **Se mide por cuándo ES la cita, no por cuándo se reservó.** Una semana de
 *   trabajo son las citas que ocurrieron en ella. La otra lectura no se pierde:
 *   vuelve como «Antelación media», que es la distancia entre las dos fechas.
 * - **Asistencia sobre lo resuelto.** Sólo cuentan las citas que ya tienen
 *   desenlace registrado. Dividir entre el total diría que un negocio con la
 *   agenda llena de citas futuras tiene un 20% de asistencia.
 * - **Los ingresos son sólo de lo prepagado.** Una cita cobrada en el local no
 *   deja ninguna orden detrás, así que no hay importe que sumar. La cifra dice
 *   de cuántas citas sale.
 */
import { useEffect, useState } from 'react'
import { SectionBar } from '../SectionBar'
import { CompareSelect, DateRangePicker, rangeLabel } from '../DateRangePicker'
import { MetricChart } from '../MetricChart'
import { MoneyMetric, panel, SplitDonut, StatCard, TopList, trend } from './analyticsShared'
import { analyticsQueryKey, getBookingsAnalytics, type AnalyticsQuery, type BookingsAnalytics } from '../../../lib/api'
import { getCachedBookingsAnalytics, setCachedBookingsAnalytics } from '../../../lib/analyticsCache'
import { failureDetail } from './offerShared'
import { useRegion } from '../../../lib/region'
import { useI18n } from '../../../lib/i18n'
import type { Language } from '@webbuilder/i18n'
import {
  IconCalendarCheck,
  IconCalendarEvent,
  IconCalendarStats,
  IconCalendarWeek,
  IconCalendarX,
  IconClock,
  IconCoins,
  IconHourglass,
  IconPlus,
  IconUserCheck,
  IconUserCog,
  IconUserPlus,
  IconUsers,
} from '@tabler/icons-react'

/** Un tanto por uno como porcentaje entero. */
function percent(ratio: number): string {
  return `${Math.round(ratio * 100)}%`
}

/**
 * Los días de la semana, lunes primero y en el idioma de quien mira.
 *
 * Era una lista fija en español. `Intl` los da en cualquier idioma; el 4 de
 * enero de 2021 fue lunes, que es por donde empieza la semana en todo el
 * panel.
 */
function weekdayNames(language: Language): string[] {
  const format = new Intl.DateTimeFormat(language, { weekday: 'long', timeZone: 'UTC' })
  return Array.from({ length: 7 }, (_, day) => format.format(Date.UTC(2021, 0, 4 + day)))
}

/**
 * Los colores del donut, atados al significado del segmento y no a su tamaño.
 *
 * La misma rampa monocroma de las otras dos pantallas: pasos del mismo tono, y
 * nunca una paleta de colores distintos compitiendo entre sí.
 */
const INK = 'var(--dash-analytics-ink)'
const INK_2 = 'var(--dash-analytics-ink-2)'
const INK_3 = 'var(--dash-analytics-ink-3)'

export function BookingsAnalyticsSection({ projectId, token, onOpenBookings, onOpenService, onOpenContact }: {
  projectId: string
  token: string
  /** Lleva a Citas, que es donde se crea una. Aquí sólo se miden. */
  onOpenBookings?: (() => void) | undefined
  /** A la ficha del servicio. Ver `TopListRow.onOpen`. */
  onOpenService?: ((offerId: string) => void) | undefined
  /** A la ficha del cliente en Contactos, por su identidad (`email:…`). */
  onOpenContact?: ((identity: string) => void) | undefined
}) {
  const i18n = useI18n()
  const { t, fmt, language } = i18n
  const { currency: projectCurrency } = useRegion()
  const count = (value: number) => fmt.number(value)
  /** «3 citas» / «1 cita»: el plural suelto se cuela en todas las listas. */
  const plural = (value: number, oneKey: string, manyKey: string) =>
    t(value === 1 ? oneKey : manyKey, { count: fmt.number(value) })
  /**
   * Minutos de agenda en palabras: «45 min», «6 h», «6 h 30 min».
   *
   * Sin abreviar a decimales —«6.5 h»— porque una agenda se piensa en horas y
   * minutos, que es como se escribió al ponerle duración a cada servicio.
   */
  const durationLabel = (minutes: number): string => {
    if (minutes < 60) return t('bookingsAn.dur.minutes', { count: count(minutes) })
    const hours = Math.floor(minutes / 60)
    const rest = minutes % 60
    return rest === 0
      ? t('bookingsAn.dur.hours', { count: count(hours) })
      : t('bookingsAn.dur.hoursMinutes', { hours: count(hours), minutes: rest })
  }
  /**
   * Una antelación en horas, dicha en la unidad que se entiende sola.
   *
   * «73.4 h» obliga a dividir mentalmente entre 24 para saber si eso es mucho.
   * A partir de dos días se dice en días, que es como lo diría cualquiera.
   */
  const leadLabel = (hours: number): string => {
    if (hours <= 0) return '—'
    if (hours < 1) return t('bookingsAn.dur.minutes', { count: Math.round(hours * 60) })
    if (hours < 48) return t('bookingsAn.dur.hours', { count: Math.round(hours) })
    const days = hours / 24
    return t('bookingsAn.dur.days', { count: days < 10 ? Math.round(days * 10) / 10 : Math.round(days) })
  }
  /**
   * Por defecto: últimos 30 días contra los 30 anteriores. La misma pregunta de
   * partida que en las otras dos —«¿voy mejor que el mes pasado?»— y no hay que
   * elegir nada para responderla.
   */
  const [query, setQuery] = useState<AnalyticsQuery>({ range: '30d', compare: 'previous' })
  const queryKey = analyticsQueryKey(query)
  const [stats, setStats] = useState<BookingsAnalytics | null>(() => getCachedBookingsAnalytics(projectId, queryKey) ?? null)
  const [loading, setLoading] = useState(() => !getCachedBookingsAnalytics(projectId, queryKey))
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const cached = getCachedBookingsAnalytics(projectId, queryKey)
    // Lo cacheado se enseña al instante; el esqueleto sólo en la primera carga
    // de verdad de este tramo. Cambiar de sección y volver no puede parpadear.
    if (cached) { setStats(cached); setLoading(false) } else { setLoading(true) }
    setError(null)
    void (async () => {
      try {
        const data = await getBookingsAnalytics(token, projectId, query)
        if (cancelled) return
        setCachedBookingsAnalytics(projectId, queryKey, data)
        setStats(data)
      } catch (err) {
        if (cancelled) return
        if (!cached) setStats(null)
        setError(failureDetail(err) || t('bookingsAn.loadFailed'))
      }
      if (!cancelled) setLoading(false)
    })()
    return () => { cancelled = true }
    // `queryKey` resume la consulta entera; `query` es un objeto nuevo en cada
    // render y dispararía el efecto sin parar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, token, queryKey])

  // Sin citas prepagadas no hay moneda que deducir: se usa en la que cobra.
  const currency = stats?.currency ?? projectCurrency
  const totals = stats?.totals
  const cashMetric = (amount: number) => <MoneyMetric amount={amount} currency={currency} />

  /**
   * Cómo se nombra el tramo contra el que se mide. Con fechas concretas en vez
   * de «periodo anterior»: un porcentaje sin saber contra qué se calculó no se
   * puede juzgar.
   */
  const comparison = stats?.comparison
    ? query.compare === 'year'
      ? t('range.compare.year').toLocaleLowerCase(language)
      : rangeLabel(i18n, { range: 'custom', from: stats.comparison.from, to: stats.comparison.to })
    : null

  const comparedTo = comparison ? [{ term: t('analyticsCommon.comparedWith'), detail: comparison }] : []

  const series = (stats?.series ?? []).map((point) => ({
    day: point.day,
    ...(point.hour === undefined ? {} : { hour: point.hour }),
    primary: point.bookings,
    secondary: point.completed,
  }))
  const compareSeries = (stats?.comparison?.series ?? []).map((point) => ({
    day: point.day,
    primary: point.bookings,
    secondary: point.completed,
  }))

  /* Sin ninguna cita en toda la vida del sitio no hay tramo que valga: cambiar
     las fechas seguiría enseñando ceros. Se dice una vez y se acaba la página. */
  const neverBooked = !loading && !error && stats?.everBooked === false

  const resources = stats?.resources ?? []
  const weekdays = weekdayNames(language)

  return (
    <div className="dash-analytics-view" style={{ display: 'flex', flexDirection: 'column', gap: 24, paddingBottom: 64 }} aria-busy={loading}>
      <SectionBar title={t('bookingsAn.title')} subtitle={t('bookingsAn.subtitle')}>
        <div className="dash-toolbar dash-layered-toolbar dash-analytics-range-toolbar">
          <DateRangePicker
            query={query}
            window={stats?.window}
            timezone={stats?.timezone}
            onChange={setQuery}
          />
          <CompareSelect query={query} onChange={setQuery} />
        </div>
      </SectionBar>

      {error ? (
        <div className="dash-analytics-content-in dash-card-description" style={{ ...panel, padding: 40, textAlign: 'center' }}>
          {error}
        </div>
      ) : neverBooked ? (
        <div className="dash-analytics-content-in dash-placeholder" style={panel}>
          <IconCalendarStats size={34} aria-hidden="true" />
          <strong>{t('bookingsAn.emptyTitle')}</strong>
          <span>{t('bookingsAn.emptyBody')}</span>
          {/*
            * Una salida, no sólo la explicación de por qué está vacío.
            *
            * Un vacío que sólo describe el vacío deja a quien llega sin nada que
            * hacer más que volverse por donde vino. La primera cita casi nunca
            * la reserva un cliente: la apunta uno mismo mientras prueba el
            * sitio, y crearla es lo único que puede llenar esta pantalla.
            *
            * Lleva a Citas en vez de abrir el alta aquí: el formulario necesita
            * los servicios, sus horarios y sus huecos libres, y traerse todo eso
            * a una pantalla de medición sería montar el alta dos veces.
            */}
          {onOpenBookings ? (
            <button type="button" className="dash-primary-btn" onClick={onOpenBookings}>
              <IconPlus size={15} aria-hidden="true" /> {t('bookingsAn.emptyCta')}
            </button>
          ) : null}
        </div>
      ) : (
        <>
          {/* Lo que se está dejando fuera del importe, dicho ANTES de la cifra y
              no debajo: quien cobra en dos monedas tiene que saber que el total
              de arriba es sólo de una antes de leerlo. */}
          {(stats?.otherCurrencies.length ?? 0) > 0 ? (
            <div className="dash-analytics-content-in dash-card-description" style={{ ...panel, padding: '12px 16px' }}>
              {t('bookingsAn.otherCurrencies.lead', { currency: currency.toUpperCase() })}{' '}
              {stats?.otherCurrencies.map((other, index) => (
                <span key={other.currency}>
                  {index > 0 ? ', ' : ''}
                  <strong>{other.formatted}</strong> {t('bookingsAn.otherCurrencies.in', { currency: other.currency.toUpperCase() })} ({plural(other.bookings, 'bookingsAn.unit.bookingOne', 'bookingsAn.unit.bookingMany')})
                </span>
              ))}
              {t('bookingsAn.otherCurrencies.tail')}
            </div>
          ) : null}

          <div className="dash-auto-grid">
            <StatCard
              label={t('bookingsAn.bookings')}
              value={count(totals?.bookings ?? 0)}
              icon={IconCalendarEvent}
              change={totals ? trend(totals.bookings, totals.bookingsPrev) : null}
              /* Sin citas en el tramo, lo dice: un «0» solo en la caja no se
                 distingue de una cifra que aún no ha cargado. */
              hint={totals && totals.upcoming > 0
                ? t('bookingsAn.bookings.upcoming', { count: count(totals.upcoming) })
                : totals && totals.bookings === 0 ? t('bookingsAn.noBookings') : undefined}
              info={{
                note: t('bookingsAn.bookings.note'),
                rows: [
                  ...comparedTo,
                  /* Los apartados caducados salen de todas las cifras, así que
                     hay que decir dónde fueron a parar: quien vea 12 citas en el
                     calendario y 10 aquí necesita saber por qué. */
                  ...(totals && totals.abandoned > 0
                    ? [{ term: t('bookingsAn.excluded'), detail: t('bookingsAn.excluded.detail', { holds: plural(totals.abandoned, 'bookingsAn.unit.holdOne', 'bookingsAn.unit.holdMany') }) }]
                    : []),
                ],
              }}
              loading={loading}
            />
            <StatCard
              label={t('bookingsAn.attendance')}
              value={totals && totals.resolved > 0 ? percent(totals.attendanceRate) : '—'}
              icon={IconUserCheck}
              change={totals ? trend(totals.attendanceRate, totals.attendanceRatePrev) : null}
              hint={totals && totals.resolved > 0
                ? t('bookingsAn.attendance.hint', { came: count(totals.completed), missed: count(totals.noShow) })
                : t('bookingsAn.attendance.none')}
              info={{ note: t('bookingsAn.attendance.note'), rows: comparedTo }}
              loading={loading}
            />
            <StatCard
              label={t('bookingsAn.cancellations')}
              value={count(totals?.cancelled ?? 0)}
              icon={IconCalendarX}
              /* Sin insignia de tendencia a propósito: la flecha verde de
                 «subió» sobre las cancelaciones diría lo contrario de lo que
                 pasó. El porcentaje sobre el total dice lo mismo sin mentir. */
              hint={totals && totals.bookings > 0
                ? t('bookingsAn.cancellations.hint', { percent: percent(totals.cancellationRate) })
                : t('bookingsAn.noBookings')}
              info={{ note: t('bookingsAn.cancellations.note') }}
              loading={loading}
            />
            <StatCard
              label={t('bookingsAn.revenue')}
              value={cashMetric(totals?.revenue ?? 0)}
              icon={IconCoins}
              change={totals ? trend(totals.revenue, totals.revenuePrev) : null}
              hint={totals ? t('bookingsAn.revenue.hint', { bookings: plural(totals.paidBookings, 'bookingsAn.unit.prepaidOne', 'bookingsAn.unit.prepaidMany') }) : undefined}
              info={{ note: t('bookingsAn.revenue.note'), rows: comparedTo }}
              loading={loading}
            />
          </div>

          <MetricChart
            series={series}
            comparison={compareSeries}
            comparisonLabel={comparison}
            granularity={stats?.granularity}
            timezone={stats?.timezone}
            loading={loading}
            title={t('bookingsAn.chart.title')}
            subtitle={stats?.granularity === 'hour' ? t('bookingsAn.chart.perHour') : t('bookingsAn.chart.perDay')}
            icon={<IconCalendarEvent size={14} />}
            primaryLabel={t('bookingsAn.chart.bookings')}
            secondaryLabel={t('bookingsAn.chart.attended')}
          />

          {/* Segunda fila: lo que matiza a la primera. Va debajo de la gráfica y
              no al lado de los totales porque son cifras de control —¿cuánto
              trabajo real fue?, ¿me reservan con tiempo?— y no el titular. */}
          <div className="dash-auto-grid">
            <StatCard
              label={t('bookingsAn.hours')}
              value={durationLabel(totals?.minutes ?? 0)}
              icon={IconClock}
              change={totals ? trend(totals.minutes, totals.minutesPrev) : null}
              hint={totals && totals.bookings === 0 ? t('bookingsAn.noBookings') : undefined}
              info={{ note: t('bookingsAn.hours.note'), rows: comparedTo }}
              loading={loading}
            />
            <StatCard
              label={t('bookingsAn.customers')}
              value={count(totals?.customers ?? 0)}
              icon={IconUsers}
              change={totals ? trend(totals.customers, totals.customersPrev) : null}
              /* Sin clientes, la frase de las listas vacías y no «0 new · 0
                 returning», que reparte una nada en dos. */
              hint={!totals ? undefined
                : totals.customers === 0 ? t('bookingsAn.noCustomers')
                : t('bookingsAn.customers.hint', { new: plural(totals.newCustomers, 'bookingsAn.unit.newOne', 'bookingsAn.unit.newMany'), returning: count(totals.returningCustomers) })}
              info={{ note: t('bookingsAn.customers.note'), rows: comparedTo }}
              loading={loading}
            />
            <StatCard
              label={t('bookingsAn.leadTime')}
              value={leadLabel(totals?.leadTimeHours ?? 0)}
              icon={IconHourglass}
              /* Reservar con más antelación no es ni bueno ni malo —depende del
                 negocio—, así que la insignia iría coloreando una preferencia
                 que no existe. El dato crudo y su comparación, nada más. */
              hint={totals && totals.bookings === 0
                ? t('bookingsAn.noBookings')
                : totals && totals.leadTimeHoursPrev > 0 ? t('bookingsAn.leadTime.before', { value: leadLabel(totals.leadTimeHoursPrev) }) : undefined}
              info={{ note: t('bookingsAn.leadTime.note'), rows: comparedTo }}
              loading={loading}
            />
            <StatCard
              label={t('bookingsAn.pending')}
              value={count(totals?.pending ?? 0)}
              icon={IconCalendarCheck}
              hint={totals && totals.pending > 0
                ? t('bookingsAn.pending.hint')
                : t('bookingsAn.pending.none')}
              info={{ note: t('bookingsAn.pending.note') }}
              loading={loading}
            />
          </div>

          <div className="dash-auto-grid is-wide">
            <TopList
              title={t('bookingsAn.topServices')}
              icon={IconCalendarStats}
              rows={(stats?.topServices ?? []).map((service) => ({
                key: service.offerId ?? `title:${service.title}`,
                label: service.title,
                sub: service.noShow > 0
                  ? t('bookingsAn.service.sub', { came: plural(service.completed, 'bookingsAn.unit.attendedOne', 'bookingsAn.unit.attendedMany'), missed: count(service.noShow) })
                  : plural(service.completed, 'bookingsAn.unit.attendedOne', 'bookingsAn.unit.attendedMany'),
                value: plural(service.bookings, 'bookingsAn.unit.bookingOne', 'bookingsAn.unit.bookingMany'),
                image: service.image,
                /* El servicio ya no existe, pero las citas ocurrieron: se
                   enseñan marcadas en vez de desaparecer del historial. */
                deleted: service.offerId === null,
                deletedHint: t('bookingsAn.serviceDeleted'),
                ...(onOpenService && service.offerId ? { onOpen: () => onOpenService(service.offerId!) } : {}),
              }))}
              empty={t('bookingsAn.noBookings')}
              loading={loading}
            />
            <TopList
              title={t('bookingsAn.topCustomers')}
              icon={IconUsers}
              rows={(stats?.topCustomers ?? []).map((customer) => ({
                key: customer.email ?? customer.name ?? 'anonymous',
                label: customer.name || customer.email || t('bookingsAn.unnamed'),
                sub: customer.name && customer.email
                  ? customer.email
                  : customer.noShow > 0 ? t('bookingsAn.customer.noShow', { count: count(customer.noShow) }) : undefined,
                value: plural(customer.bookings, 'bookingsAn.unit.bookingOne', 'bookingsAn.unit.bookingMany'),
                ...(onOpenContact && customer.email
                  ? { onOpen: () => onOpenContact(`email:${customer.email!.toLocaleLowerCase()}`) }
                  : {}),
              }))}
              empty={t('bookingsAn.noCustomers')}
              loading={loading}
            />
            {/* Cómo acabaron: es la pregunta de la pantalla y no está entera en
                ninguna cifra de arriba, que dan asistencia y cancelaciones por
                separado. Aquí se ve el reparto de un vistazo.

                Tres sectores y no cinco: las que están por venir y las que
                siguen sin marcar no son un desenlace, y meterlas aquí obligaría
                a repetir colores de la rampa —que sólo tiene tres pasos— con
                dos parejas de sectores del mismo tono en el mismo donut. Cada
                una tiene ya su propia cifra arriba. */}
            <SplitDonut
              title={t('bookingsAn.outcome')}
              icon={IconCalendarCheck}
              slices={[
                { key: 'completed', label: t('bookingsAn.outcome.attended'), value: totals?.completed ?? 0, color: INK },
                { key: 'no_show', label: t('bookingsAn.outcome.noShow'), value: totals?.noShow ?? 0, color: INK_2 },
                { key: 'cancelled', label: t('bookingsAn.outcome.cancelled'), value: totals?.cancelled ?? 0, color: INK_3 },
              ]}
              empty={t('bookingsAn.outcome.empty')}
              unit={t('bookingsAn.unit.bookings')}
              loading={loading}
            />
            {/* El ritmo de la semana, en su orden natural y no de mayor a menor:
                lo que se busca aquí es la FORMA —dónde se acumula y qué día está
                muerto—, y ordenarlo por tamaño la deshace. */}
            <TopList
              title={t('bookingsAn.weekdays')}
              icon={IconCalendarWeek}
              /* Los siete días vienen siempre, con ceros. Sin ninguna cita el
                 tramo, eso serían siete renglones de «0 citas» diciendo nada:
                 se cede el sitio al mensaje de vacío, como en las otras listas. */
              rows={(totals?.bookings ?? 0) === 0 ? [] : (stats?.weekdays ?? []).map((entry) => ({
                key: `weekday:${entry.weekday}`,
                label: weekdays[entry.weekday] ?? '—',
                value: plural(entry.bookings, 'bookingsAn.unit.bookingOne', 'bookingsAn.unit.bookingMany'),
              }))}
              empty={t('bookingsAn.noBookings')}
              loading={loading}
            />
            {/* Sólo con más de uno: en un negocio de una persona esta lista es
                una fila con el total de arriba repetido. */}
            {resources.length > 1 ? (
              <TopList
                title={t('bookingsAn.resources')}
                icon={IconUserCog}
                rows={resources.map((resource) => ({
                  key: resource.resourceId,
                  label: resource.name,
                  value: plural(resource.bookings, 'bookingsAn.unit.bookingOne', 'bookingsAn.unit.bookingMany'),
                }))}
                empty={t('bookingsAn.noBookings')}
                loading={loading}
              />
            ) : null}
            {/* Nuevos contra recurrentes: es lo que distingue una agenda que
                vive de captar cada semana de una que vive de que la gente
                vuelva. Mismo donut y mismas etiquetas que en Ventas, que mide
                exactamente lo mismo sobre los compradores. */}
            <SplitDonut
              title={t('bookingsAn.newVsReturning')}
              icon={IconUserPlus}
              slices={[
                { key: 'new', label: t('bookingsAn.new'), value: totals?.newCustomers ?? 0, color: INK },
                { key: 'returning', label: t('bookingsAn.returning'), value: totals?.returningCustomers ?? 0, color: INK_2 },
              ]}
              empty={t('bookingsAn.noCustomers')}
              unit={t('bookingsAn.unit.customers')}
              loading={loading}
            />
          </div>
        </>
      )}
    </div>
  )
}
